import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from 'usehooks-ts'
import { PageHeader } from '../../tailwindComponents/PageHeader'
import { NETWORK_RATECARD_CREATE } from '../../constants'
import {
  createEmptyZone,
  getDefaultPalletCaps,
  getServiceKey,
  lockFootprint,
  DEFAULT_SERVICE,
  NetworkRateCardZone,
  PALLET_UNIT,
  PalletDimensions,
  ServiceIdentity,
  Surcharge,
  DEFAULT_NETWORK_RATECARD_CURRENCY,
} from './types'
import { normaliseImportedService } from './normaliseImportedService'
import { fillEverySurchargeType } from './fillEverySurchargeType'
import { DEFAULT_COUNTRY } from './countries'
import { NetworkRatecardEditor } from './NetworkRatecardEditor'
import { expandPostcodeInput } from './parsePostcodeRanges'
import { useImportedNetworkRatecard } from './useImportedNetworkRatecard'

interface ImportedPalletDimensions {
  height?: number
  width?: number
  depth?: number
  weight?: number
}

function mergeImportedCaps(
  palletType: string | undefined,
  imported: ImportedPalletDimensions | undefined
): PalletDimensions | undefined {
  const defaults = getDefaultPalletCaps(palletType)
  if (!imported && !defaults) return undefined
  return lockFootprint(palletType, {
    height: imported?.height ?? defaults?.height ?? null,
    width: imported?.width ?? defaults?.width ?? null,
    depth: imported?.depth ?? defaults?.depth ?? null,
    weight: imported?.weight ?? defaults?.weight ?? null,
  })
}

export function AddNetworkRatecard() {
  const navigate = useNavigate()
  useDocumentTitle('FLOX - Add Network Rate Card')
  const importedData = useImportedNetworkRatecard()

  // Map imported data to NetworkRateCardZone[] if present
  const { initialZones, initialName, initialCountry, initialCurrency, initialSurcharges } = useMemo(() => {
    if (!importedData) {
      return {
        initialZones: [createEmptyZone('Zone 1', [DEFAULT_SERVICE], DEFAULT_COUNTRY.code)],
        initialName: '',
        initialCountry: DEFAULT_COUNTRY.code,
        initialCurrency: DEFAULT_NETWORK_RATECARD_CURRENCY,
        initialSurcharges: fillEverySurchargeType([]),
      }
    }

    const identitiesByKey = new Map<string, ServiceIdentity>()
    const normalisedZones = importedData.zones.map((z) => ({
      zone: z,
      services: z.services.map((imported) => {
        const normalised = normaliseImportedService(imported)
        const identity: ServiceIdentity = {
          serviceName: normalised.serviceName,
          unit: PALLET_UNIT,
          ...(normalised.palletType ? { palletType: normalised.palletType } : {}),
        }
        const key = getServiceKey(identity)
        if (!identitiesByKey.has(key)) identitiesByKey.set(key, identity)
        return { imported, key }
      }),
    }))
    const identities = Array.from(identitiesByKey.values())

    const zones: NetworkRateCardZone[] = normalisedZones.map(({ zone: z, services }) => {
      // Copilot emits source-format postcode tokens (e.g. ["AL", "CB1-5", "61-64", "PE38+"]).
      // Expand them through the same parser the manual input field uses so prefix carry-forward,
      // ranges, and `+` open-ends all flatten correctly into discrete postcode prefixes.
      // The compact source string is preserved on `originalPostcodesInput` so the editor can show
      // a "Imported as: …" subtitle and the user can still recognise their source ratecard.
      const sourceInput =
        z.originalPostcodesInput ?? (z.postcodes ?? []).join(', ')
      const { postcodes: expanded } = expandPostcodeInput(sourceInput)
      return {
        id: crypto.randomUUID(),
        zoneName: z.zoneName,
        countryCode: z.countryCode || 'GBR',
        // Status starts at 'pending' — the editor's auto-resolve effect flips them to 'resolved',
        // 'not_found', or 'error' as the backend responds. Save-side validation refuses anything
        // that isn't 'resolved' (or unset, for manually-entered rows).
        locations: expanded.map(p => ({
          prefix: p,
          city: null,
          geoPoint: null,
          status: 'pending' as const,
        })),
        servicePricing: identities.map((identity) => {
          const match = services.find((s) => s.key === getServiceKey(identity))
          const palletDimensions = mergeImportedCaps(identity.palletType, match?.imported.palletDimensions)
          return {
            serviceName: identity.serviceName,
            unit: identity.unit,
            breaks: [{
              min: 1,
              max: null,
              price: match?.imported.price ?? 0,
            }],
            ...(identity.palletType ? { palletType: identity.palletType } : {}),
            ...(palletDimensions ? { palletDimensions } : {}),
          }
        }),
        originalPostcodesInput: sourceInput,
      }
    })

    // Map imported surcharges (from the Copilot extractor) to the editor's Surcharge shape.
    // Each surcharge gets a fresh client-side id so the SurchargesPanel can address rows by id
    // for in-place edit/remove without depending on positional indices that shift on add/remove.
    const importedSurcharges: Surcharge[] = (importedData.surcharges ?? []).map((s) => ({
      id: crypto.randomUUID(),
      name: s.name,
      price: Number.isFinite(s.price) ? s.price : null,
      ...(s.unit ? { unit: s.unit } : {}),
      ...(s.notes ? { notes: s.notes } : {}),
    }))

    return {
      initialZones: zones,
      initialName: importedData.networkName || '',
      initialCountry: importedData.countryCode || DEFAULT_COUNTRY.code,
      // Imported currency from the Copilot extractor; falls back to GBP (the platform default)
      // when the source document didn't state one. The editor still lets the user override.
      initialCurrency: importedData.currency || DEFAULT_NETWORK_RATECARD_CURRENCY,
      initialSurcharges: fillEverySurchargeType(importedSurcharges),
    }
  }, [importedData])

  const wingmanNotes = useMemo(
    () => (importedData ? Array.from(new Set(importedData.extractionWarnings ?? [])) : undefined),
    [importedData]
  )

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Network Tariffs', href: '/transport-price-master', current: false },
    { name: 'Add Network Rate Card', href: '#', current: true },
  ]

  return (
    <div className='flex flex-col h-[calc(100vh-64px)]'>
      <PageHeader header='Add Network Rate Card' pages={pages} />
      <NetworkRatecardEditor
        // Force a fresh editor instance whenever a new import lands. Without this, navigating
        // to /network-ratecard/add while already on the page (Copilot import re-fire) updates
        // the atom but leaves the editor's internal `useState(initialZones)` stuck on the first
        // render's values.
        key={importedData?.id ?? 'fresh-network-ratecard'}
        mode='create'
        initialNetworkName={initialName}
        initialCountryCode={initialCountry}
        initialCurrency={initialCurrency}
        initialEffectiveFrom=''
        initialEffectiveTo=''
        initialNotes=''
        initialIsActive={true}
        initialIsLocalRatecard={false}
        initialZones={initialZones}
        initialSurcharges={initialSurcharges}
        wingmanNotes={wingmanNotes}
        saveEndpoint={NETWORK_RATECARD_CREATE}
        saveButtonLabel='Save Network Rate Card'
        onSaveSuccess={() => navigate('/transport-price-master')}
        onCancel={() => navigate('/transport-price-master')}
      />
    </div>
  )
}
